/**
 * Creator videos produced for the brands we represent.
 *
 * `views` is null where the platform doesn't expose a public view count on the
 * embed (Instagram Reels), so the tile hides the stat instead of printing a 0.
 */
import { BRANDS, type BrandSlug } from './brands';
import type { Audience } from './briefs';

export type Platform = 'tiktok' | 'instagram';

/** Keys into BRANDS-by-slug; the carousel credits the active video's brand. */
export type { BrandSlug };

export interface ShowcaseVideo {
  id: string;
  handle: string;
  platform: Platform;
  brand: BrandSlug;
  /**
   * Audience-specific brand briefs allowed to show this video.
   *
   * Omit for examples that apply to every brief. This does not affect the
   * public portfolio carousel, which is intentionally audience-agnostic.
   */
  audiences?: Audience[];
  src: string;
  poster: string;
  views: number | null;
  likes: number | null;
  /**
   * Why this video is the one to study, in the brief for its brand.
   *
   * At most one video per brand should carry this. Raw engagement already
   * shows on every tile, so this is for the reason the numbers do not give —
   * usually that it hit the product the brand actually sells. The brief pulls
   * it out of the grid and leads with it.
   */
  feature?: { title: string; note: string };
}

/**
 * Name and link per slug, derived from BRANDS rather than repeated here — the
 * two lists had already become two places to rename a brand.
 */
export const SHOWCASE_BRANDS = Object.fromEntries(
  BRANDS.map((brand) => [brand.slug, { name: brand.name, url: brand.url }]),
) as Record<BrandSlug, { name: string; url: string }>;

/**
 * Alternating brand order so the carousel reads as a mixed portfolio, led by
 * the highest-engagement piece of each pair.
 */
export const SHOWCASE_VIDEOS: ShowcaseVideo[] = [
  {
    id: 'roxy-paulalamc-mods',
    audiences: ['gaming'],
    handle: '@paulalamc',
    platform: 'tiktok',
    brand: 'roxy',
    src: '/showcase/roxy-paulalamc-mods-h264.mp4',
    poster: '/showcase/roxy-paulalamc-mods.jpg',
    views: 301000,
    likes: 33200,
    feature: {
      title: 'Start here',
      note: "Our best-performing Roxy video. It works because the mod is the point: she names one absurd idea, builds it on camera, then loads the game and plays it. The build is the entertainment, not an interruption to it. Copy the structure, not the mod.",
    },
  },
  {
    id: 'roxy-nicochelo-jenny',
    audiences: ['gaming'],
    handle: '@not_nicochelo21',
    platform: 'tiktok',
    brand: 'roxy',
    src: '/showcase/roxy-nicochelo-jenny-h264.mp4',
    poster: '/showcase/roxy-nicochelo-jenny.jpg',
    views: 226400,
    likes: 27000,
  },
  {
    id: 'roxy-nicochelo-aura',
    audiences: ['gaming'],
    handle: '@not_nicochelo21',
    platform: 'tiktok',
    brand: 'roxy',
    src: '/showcase/roxy-nicochelo-aura-h264.mp4',
    poster: '/showcase/roxy-nicochelo-aura.jpg',
    views: 36200,
    likes: 2893,
  },
  {
    id: 'red-moon-ai',
    handle: '@red.moon.ai',
    platform: 'tiktok',
    brand: 'mila',
    src: '/showcase/red-moon-ai.mp4',
    poster: '/showcase/red-moon-ai.jpg',
    views: 511200,
    likes: 36900,
  },
  {
    id: 'hiding-pampisman',
    handle: '@pampisman',
    platform: 'instagram',
    brand: 'hiding',
    src: '/showcase/hiding-pampisman.mp4',
    poster: '/showcase/hiding-pampisman.jpg',
    views: null,
    likes: 52803,
    feature: {
      title: 'Start here',
      note: "This is the exam one — one of the hooks the brand asks for, and our best video for them by a long way. It works because it shows the AI on screen while the exam is on screen, so there is nothing to take on trust. Copy the idea, not the video: show it solving something real, live.",
    },
  },
  {
    id: 'samxnthastudy',
    handle: '@samxnthastudy',
    platform: 'tiktok',
    brand: 'mila',
    src: '/showcase/samxnthastudy.mp4',
    poster: '/showcase/samxnthastudy.jpg',
    views: 257000,
    likes: 12300,
  },
  {
    id: 'hiding-liatokkk',
    handle: '@liatokkk',
    platform: 'instagram',
    brand: 'hiding',
    src: '/showcase/hiding-liatokkk.mp4',
    poster: '/showcase/hiding-liatokkk.jpg',
    views: null,
    likes: 21162,
  },
  {
    id: 'sergiozulueta',
    handle: '@sergiozulueta',
    platform: 'tiktok',
    brand: 'mila',
    src: '/showcase/sergiozulueta.mp4',
    poster: '/showcase/sergiozulueta.jpg',
    views: 19400,
    likes: 2130,
  },
  {
    id: 'hiding-techwmiguel',
    handle: '@techwmiguel',
    platform: 'instagram',
    brand: 'hiding',
    src: '/showcase/hiding-techwmiguel.mp4',
    poster: '/showcase/hiding-techwmiguel.jpg',
    views: null,
    likes: 2376,
  },
  {
    id: 'hiding-z3kaly',
    handle: '@z3kaly',
    platform: 'tiktok',
    brand: 'hiding',
    src: '/showcase/hiding-z3kaly.mp4',
    poster: '/showcase/hiding-z3kaly.jpg',
    views: null,
    likes: 2004,
  },
  {
    id: 'hiding-cotypiozzi',
    handle: '@cotypiozzi',
    platform: 'tiktok',
    brand: 'hiding',
    src: '/showcase/hiding-cotypiozzi.mp4',
    poster: '/showcase/hiding-cotypiozzi.jpg',
    views: null,
    likes: 1807,
  },
  {
    id: 'reel-dwz1fzxggva',
    handle: '@studygram_dig',
    platform: 'instagram',
    brand: 'mila',
    src: '/showcase/reel-dwz1fzxggva.mp4',
    poster: '/showcase/reel-dwz1fzxggva.jpg',
    views: null,
    likes: 1284,
    feature: {
      title: 'Start here',
      note: 'This one is about Sheets — the spreadsheet. That is the part of Mila most people pay for, so videos about it sell the best. Copy the idea, not the video: pick one real thing you do in a spreadsheet and show it.',
    },
  },
  {
    id: 'hiding-thelusho',
    handle: '@the.lusho',
    platform: 'tiktok',
    brand: 'hiding',
    src: '/showcase/hiding-thelusho.mp4',
    poster: '/showcase/hiding-thelusho.jpg',
    views: null,
    likes: 991,
  },
  {
    id: 'hiding-yesthisispearl',
    handle: '@yesthisispearl',
    platform: 'tiktok',
    brand: 'hiding',
    src: '/showcase/hiding-yesthisispearl.mp4',
    poster: '/showcase/hiding-yesthisispearl.jpg',
    views: null,
    likes: 899,
  },
  {
    id: 'reel-dv3zzhvdgpj',
    handle: '@studywithdaii',
    platform: 'instagram',
    brand: 'mila',
    src: '/showcase/reel-dv3zzhvdgpj.mp4',
    poster: '/showcase/reel-dv3zzhvdgpj.jpg',
    views: null,
    likes: 176,
  },
  {
    id: 'reel-dv3aegiket',
    handle: '@dailoghanni_study',
    platform: 'instagram',
    brand: 'mila',
    src: '/showcase/reel-dv3aegiket.mp4',
    poster: '/showcase/reel-dv3aegiket.jpg',
    views: null,
    likes: null,
  },
  {
    id: 'studywithme111111111',
    handle: '@studywithme111111111',
    platform: 'tiktok',
    brand: 'mila',
    src: '/showcase/studywithme111111111.mp4',
    poster: '/showcase/studywithme111111111.jpg',
    views: 593,
    likes: 49,
  },
];

/** Videos allowed in one audience-specific creator brief. */
export function findBriefVideos(slug: BrandSlug, audience?: Audience): ShowcaseVideo[] {
  return SHOWCASE_VIDEOS.filter(
    (video) =>
      video.brand === slug &&
      (!video.audiences || (audience ? video.audiences.includes(audience) : false)),
  );
}
